import { NoId } from 'app/tools/general.types';
import { Except, Merge } from 'type-fest';
import { AssignedProduct } from '../product-assignment/api-product-assignment.types';
// eslint-disable-next-line import/no-cycle
import {
  CompanyIdentity,
  UserIdentity,
} from '../user/api-user.types';

export type RepairerSearchReq = {
  assignedProductId: string;
  lat: number;
  long: number;
  maxDistance: number;
};

type RepairLocationCore = {
  id: number;
  locationDisplayName: string;
  addrLine1: string;
  addrLine2?: string;
  city: string;
  postcode: string;
  latitude: number;
  longitude: number;
  openCaseId?: number;
  repairer?: number;
  repairerDetails?: CompanyIdentity;
};

export type RepairLocation = RepairLocationCore & {
  addrLine1: string;
  addrLine2?: string;
  city?: string;
  postcode: string;
};

export type RepairAccountType = {
  companyacctype: string;
};

export type NotificationUpdateRequest = {
  enablePushNotification: boolean;
};

export type RepairPersonalDetail = {
  dob: string;
  firstName?: string;
  lastName?: string;
  currency?: string;
  phoneNumber?: string;
  website?: string;
  accountholdername: string;
  companyacctype: string;
};

export type CreateRepairLocationReq = Except<
  NoId<RepairLocation>,
  'repairer' | 'repairerDetails' | 'repairer'
>;

export type RepairerSearchItem = RepairLocationCore & {
  distance: number;
  repairer: number;
  repairerDetails: CompanyIdentity;
  // Things to be discussed
  occupation?: string;
  rating?: number;
  ratingCount?: number;
  specification?: string;
  // location: LocationProps;
};

export type RepairerSearchRtn = {
  fallback: Merge<CompanyIdentity, { overview: string }>;
  searchResults: RepairerSearchItem[];
};

// export type ChatItem = {
//   name: string;
//   lastChat: string;
//   status: string;
//   date: string;
//   userId: string;
//   profilePhotoUrl: string;
// };

export type InitiateChatReq = {
  productDefect: number;
  repairer: number;
  id: number;
};

// Repairer Bank Status - missing fields
export type BankStatus = {
  name: string;
  companyNumber: string;
  vatNumber: string;
  phone: string;
  website: string;
};

//
export type RepairCase = {
  id: number;
  productDefect: number;
  paymentStatus: string;
  chatListStatus: string;
  productDefectDetails: ProductDefectDetails;
  repairer: number;
  repairerDetails: CompanyIdentity;
  userDetails: UserIdentity;
  events: EventEntity[];
  lastEvent: EventEntity;
  status: CaseStatus;
  quickActions: QuickActionTemplate[];
  quotationAvailability: boolean;
  repairCompleted: boolean;
  repairQuoteAgreed: boolean;
  repairStarted: boolean;
  caseStatus: string;
  caseTail: CaseTail[];
  warrantyCovered: boolean;
  repairerComments: string;
  unreadCountConsumer: number;
  unreadCountRepairer: number;
  isRepairPaymentDone: string;
  isDiagnosisPaymentDone: string;
  chatListNewCount: number;
  chatListInProgressCount: number;
  chatListCompleteCount: number;
  diagnosisComment: string;
  additionalComment: string;
  ratingGiven: boolean;
};
export type APIErrors = {
  ErrorMessage: string;
};
export type CaseTail = {
  date: string;
  status: string;
};
export type QuoteSummery = {
  id: number;
  diagnosisQuoteAgreed: boolean;
  pickUpDate: string;
  repairCompleted: boolean;
  repairQuoteAgreed: boolean;
  repairStarted: boolean;
  isWithWarranty: boolean;
  diagnosisQuote: DiagnosisQuote[];
  repairQuote: DiagnosisQuote[];
};
export type DiagnosisQuote = {
  quotationAmount: QuotationAmount;
  quoteDetails: QuoteDetails[];
  declined?: boolean;
};
export type QuotationAmount = {
  amount: number;
  currency: string;
};
export type QuoteSummeryUpdate = {
  id: number;
  pickUpDate: string;
  dropOffDate: string;
  deliveryLocation: number;
  collectionCharges?: QuoteSummeryCollectionCharges;
};
export type QuotePayment = {
  repairCaseId: number;
  paymentMethodId: string;
  amount: string;
  currency: string;
};
export type QuoteSummeryCollectionCharges = {
  amount: number;
  currency: string;
};
export type RepairStatusUpdate = {
  repairCase: number;
  status: string;
};
export type PaymentMethod = {
  id: string;
  number: number;
  expMonth: number;
  expYear: number;
  cvc: number;
  name: string;
  brand: string;
  nameOnCard: string;
  paymentMethodId: string;
  last4: string;
};
export type PaymentMethodResp = {
  data: PaymentMethod[];
  statusFlag: string;
};
export type ProductDefectDetails = {
  id: number;
  assignedProduct: number;
  assignedProductDetails: AssignedProduct;
  claim: Claim;
  title: string;
  description: string;
};
export type Claim = {
  id: number;
  defect: number;
  date: string;
};
export type QuoteDetails = {
  description: string;
  qty: number;
  total: number;
};

export type CurrencyObject = { currency: string; amount: number };
export type ChatActionData = {
  quotationAmount?: CurrencyObject;
  quoteDetails?: QuoteDetails[];
};

export type SendChatEvent = {
  repairCase: number;
  displayText: string;
  eventType: ChatEventType;
  actionData?: ChatActionData;
};

export type WarrantyClaimEvent = {
  repaircaseId: number;
};
export type WarrantyClaimPayment = {
  repairCaseId: number;
};

export type SendChatImageEvent = {
  repairCase: number;
  eventType: ChatEventType;
  image: File;
};

export type SendChatData = {
  displayText: string;
  eventType?: ChatEventType;
  actionData?: ChatActionData;
  image?: File | null;
};

export type EventEntity = {
  id?: number;
  repairCase?: number;
  displayText: string;
  eventType: ChatEventType;
  botAuthored?: boolean;
  actionData?: ChatActionData;
  imageUrl?: string;
  quickActions?: QuickActionTemplate[];
  actionsResolved?: boolean;
  created?: string;
};
export type QuickActionTemplate = {
  displayText: string;
  messageText?: string;
  submissionAction: ChatEventType;
};

export enum ChatEventType {
  CONSUMER_MESSAGE = 'consumer_message',
  REPAIRER_MESSAGE = 'repairer_message',
  BOT_MESSAGE = 'bot_message',
  WARRANTY_CLAIM = 'warranty_claim',
  CONTEXT_UPDATE = 'context_update',
  // Quotes phase
  DIAGNOSIS_QUOTE = 'diagnosis_quote',
  DIAGNOSIS_QUOTE_AGREED = 'diagnosis_quote_agreed',
  DIAGNOSIS_QUOTE_DECLINE = 'diagnosis_quote_decline',
  DIAGNOSIS_QUOTE_ACCEPT = 'diagnosis_quote_accept',
  REPAIR_QUOTE = 'repair_quote',
  REPAIR_QUOTE_AGREED = 'repair_quote_agreed',
  REPAIR_QUOTE_DECLINE = 'repair_quote_decline',
  REPAIR_QUOTE_ACCEPT = 'repair_quote_accept',
  // Repair phase
  REPAIR_STARTED = 'repair_started',
  REPAIR_COMPLETED = 'repair_completed',
  REPAIR_FAILED = 'repair_failed',
  // Post repair
  CONSUMER_REVIEW = 'consumer_review',
  // Payment done
  CONSUMER_PAYMENT_DONE = 'payment_done',
  // Early exits
  CONSUMER_ABANDONED = 'consumer_abandoned',
  REPAIRER_ABANDONED = 'repairer_abandoned',
}

export const quotingMsgTypes = [
  ChatEventType.DIAGNOSIS_QUOTE,
  ChatEventType.REPAIR_QUOTE,
] as const;

export const nonActionMsgTypes = [
  ChatEventType.CONSUMER_MESSAGE,
  ChatEventType.REPAIRER_MESSAGE,
  ChatEventType.CONTEXT_UPDATE,
  ChatEventType.BOT_MESSAGE,
] as const;

export type QuoteActionType = typeof quotingMsgTypes[number];
export type NonActionMsgType = typeof nonActionMsgTypes[number];

export type QuoteActionTemplate = QuickActionTemplate & {
  submissionAction: QuoteActionType;
};

export enum CaseStatus {
  IN_PROGRESS = 'in_progress',
  NEW = 'new',
  RESOLVED = 'resolved',
  COMPLETE = 'item_collected',
  FAILED = 'failed',
  ABANDONED = 'abandoned',
}

export type TransactionQuote = {
  productName: string;
  date: string;
  time: string;
  consumerName: string;
  subtotal: number;
  total: number;
  type: string;
  repairQuote: Quote[];
  diagnosisQuote: DiagnosticsQuote[];
  id: number;
  imageUrl: string;
  applicationFee: string;
  repairCaseDescription: string;
};

export type DiagnosticsQuote = {
  quotationAmount: {
    amount: string;
    currency: string;
  };
};
export type Quote = {
  quotationAmount: {
    itemName: string;
    amount: string;
    quantity: string;
    currency: string;
  };
  quoteDetails: QuoteDetails[];
};
